import React, { useState } from "react";

/**
 * Integrated Science — Chapter 2 (Motion → Energy)
 * Jeopardy Review Game: Lessons 14–16
 *   • Lesson 14 — Work, Power, Potential Energy
 *   • Lesson 15 — Kinetic Energy & the Work–Energy Theorem
 *   • Lesson 16 — Conservation of Energy
 *
 * Self-contained: React only, no external dependencies.
 * Design language: navy / gold / cream, Cambria headings, Calibri body.
 */

const PALETTE = {
  navy: "#0b1e3f",
  navyDeep: "#071630",
  cell: "#10285a",
  cellHover: "#1b3c82",
  gold: "#d4af37",
  goldSoft: "#e8cf7a",
  cream: "#f6f1e3",
  terracotta: "#c56a4f",
  scienceBlue: "#4a90c2",
  correct: "#3f8f5f",
  wrong: "#b24a4a",
};

const FONT_HEAD = "Cambria, Georgia, 'Times New Roman', serif";
const FONT_BODY = "Calibri, 'Segoe UI', system-ui, sans-serif";

// ---------------------------------------------------------------------------
// GAME DATA
// ---------------------------------------------------------------------------
const VALUES = [200, 400, 600, 800, 1000];

const CATEGORIES = [
  {
    title: "WORK IT OUT",
    clues: [
      { clue: "The scientific definition of work is force multiplied by this.", answer: "What is distance (displacement in the direction of the force)?" },
      { clue: "Work — and every form of energy — is measured in this SI unit.", answer: "What is the joule (J)?" },
      { clue: "You hold a heavy box perfectly still for two minutes. This is how much work you do on the box.", answer: "What is zero? (No displacement = no work.)" },
      { clue: "This is the equation that defines work.", answer: "What is W = F × d?" },
      { clue: "A student pushes a crate with 50 N of force across 3 meters of floor. This much work is done.", answer: "What is 150 joules? (50 × 3)" },
    ],
  },
  {
    title: "POWER HOUR",
    clues: [
      { clue: "Power measures how FAST this gets done.", answer: "What is work? (Power is the rate of doing work.)" },
      { clue: "The SI unit of power, equal to one joule per second.", answer: "What is the watt (W)?" },
      { clue: "This is the equation for power in terms of work and time.", answer: "What is P = W ÷ t?" },
      { clue: "Two students each do 600 J of work climbing the same stairs — one runs, one walks. The runner has more of this.", answer: "What is power? (Same work in less time.)" },
      { clue: "A motor does 1,200 J of work in 4 seconds. This is its power output.", answer: "What is 300 watts? (1,200 ÷ 4)" },
    ],
  },
  {
    title: "STORED UP",
    clues: [
      { clue: "Energy that is stored and waiting to do work — because of position or condition — is called this.", answer: "What is potential energy?" },
      { clue: "Gravitational potential energy depends on an object's weight (mass) and this.", answer: "What is height (how high it is)?" },
      { clue: "These are the three quantities in the gravitational PE formula PE = mgh.", answer: "What are mass, gravity (g), and height?" },
      { clue: "You lift a 2 kg book 1.5 m onto a shelf (use g = 10 m/s²). This is its gravitational PE now.", answer: "What is 30 joules? (2 × 10 × 1.5)" },
      { clue: "All of the energy on the Earth originates from here.", answer: "What is the sun?" },
    ],
  },
  {
    title: "IN MOTION",
    clues: [
      { clue: "The energy an object has because it is moving.", answer: "What is kinetic energy?" },
      { clue: "This is the formula for kinetic energy.", answer: "What is KE = ½ m v²?" },
      { clue: "You double a car's speed. Because speed is squared in the formula, its kinetic energy does this.", answer: "What is quadruple (increase by 4×)?" },
      { clue: "The work–energy theorem says the net work done on an object equals the change in this.", answer: "What is its kinetic energy?" },
      { clue: "Find the kinetic energy of a 4 kg ball moving at 3 m/s.", answer: "What is 18 joules? (½ × 4 × 3²)" },
    ],
  },
  {
    title: "CAN'T DESTROY IT",
    clues: [
      { clue: "The law of conservation of energy says energy cannot be created or this.", answer: "What is destroyed?" },
      { clue: "Instead of being created or destroyed, energy can only be transferred or do this.", answer: "What is transformed (change form)?" },
      { clue: "At the very top of its swing, a pendulum's energy is almost all this type.", answer: "What is potential energy?" },
      { clue: "When an archer releases the bowstring, elastic PE transforms mainly into THIS energy of the flying arrow.", answer: "What is kinetic energy?" },
      { clue: "A coaster car has 50,000 J of PE at the top of the first hill. Ignoring friction, this is its KE at the bottom.", answer: "What is 50,000 joules? (Energy is conserved.)" },
    ],
  },
];

const FINAL = {
  category: "ENERGY ALL AROUND",
  clue: "A 1,000 kg car travels at 20 m/s. Using KE = ½mv², calculate the car's kinetic energy.",
  answer: "What is 200,000 joules? (½ × 1,000 × 20² = ½ × 1,000 × 400)",
};

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------
export default function Jeopardy() {
  const [screen, setScreen] = useState("title"); // title | board | final
  const [teams, setTeams] = useState([
    { name: "Team 1", score: 0 },
    { name: "Team 2", score: 0 },
    { name: "Team 3", score: 0 },
  ]);
  const [answered, setAnswered] = useState({}); // "c-r": true
  const [active, setActive] = useState(null); // { c, r }
  const [revealed, setRevealed] = useState(false);

  // Final Jeopardy state
  const [wagers, setWagers] = useState({});
  const [finalReveal, setFinalReveal] = useState(false);
  const [finalAnswer, setFinalAnswer] = useState(false);

  const openClue = (c, r) => {
    if (answered[`${c}-${r}`]) return;
    setActive({ c, r });
    setRevealed(false);
  };

  const closeClue = () => {
    if (active) setAnswered((a) => ({ ...a, [`${active.c}-${active.r}`]: true }));
    setActive(null);
    setRevealed(false);
  };

  const award = (teamIdx, delta) => {
    setTeams((t) =>
      t.map((team, i) => (i === teamIdx ? { ...team, score: team.score + delta } : team))
    );
  };

  const adjustTeamCount = (n) => {
    setTeams((prev) => {
      const next = [...prev];
      while (next.length < n) next.push({ name: `Team ${next.length + 1}`, score: 0 });
      return next.slice(0, n);
    });
  };

  const renameTeam = (i, name) =>
    setTeams((t) => t.map((team, idx) => (idx === i ? { ...team, name } : team)));

  const resetGame = () => {
    setAnswered({});
    setActive(null);
    setRevealed(false);
    setWagers({});
    setFinalReveal(false);
    setFinalAnswer(false);
    setTeams((t) => t.map((team) => ({ ...team, score: 0 })));
    setScreen("title");
  };

  const allAnswered = Object.keys(answered).length >= CATEGORIES.length * VALUES.length;

  // -------------------------------------------------------------------------
  return (
    <div style={styles.root}>
      <style>{globalCss}</style>

      {/* ---------------- TITLE SCREEN ---------------- */}
      {screen === "title" && (
        <div style={styles.titleWrap}>
          <div style={styles.titleCard}>
            <div style={styles.kicker}>Integrated Science · Chapter 4</div>
            <h1 style={styles.bigTitle}>JEOPARDY!</h1>
            <div style={styles.subTitle}>The Energy Unit — Lessons 14–16</div>
            <div style={styles.topicRow}>
              <span style={styles.topicPill}>Work &amp; Power</span>
              <span style={styles.topicPill}>Potential Energy</span>
              <span style={styles.topicPill}>Kinetic Energy</span>
              <span style={styles.topicPill}>Conservation of Energy</span>
            </div>

            <div style={styles.setupBox}>
              <div style={styles.setupLabel}>How many teams?</div>
              <div style={styles.teamBtnRow}>
                {[2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => adjustTeamCount(n)}
                    style={{
                      ...styles.teamCountBtn,
                      ...(teams.length === n ? styles.teamCountBtnActive : {}),
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <div style={styles.nameGrid}>
                {teams.map((t, i) => (
                  <input
                    key={i}
                    value={t.name}
                    onChange={(e) => renameTeam(i, e.target.value)}
                    style={styles.nameInput}
                    aria-label={`Team ${i + 1} name`}
                  />
                ))}
              </div>
            </div>

            <button onClick={() => setScreen("board")} style={styles.playBtn}>
              Start Game →
            </button>
          </div>
        </div>
      )}

      {/* ---------------- GAME BOARD ---------------- */}
      {screen === "board" && (
        <div style={styles.boardWrap}>
          <header style={styles.boardHeader}>
            <h2 style={styles.boardTitle}>Energy Jeopardy · Lessons 14–16</h2>
            <div style={styles.headerBtns}>
              {allAnswered && (
                <button onClick={() => setScreen("final")} style={styles.finalJumpBtn}>
                  ★ Final Jeopardy
                </button>
              )}
              <button onClick={() => setScreen("final")} style={styles.ghostBtn}>
                Final Jeopardy
              </button>
              <button onClick={resetGame} style={styles.ghostBtn}>
                Reset
              </button>
            </div>
          </header>

          <div style={styles.grid}>
            {CATEGORIES.map((cat, c) => (
              <div key={c} style={styles.catHead}>
                {cat.title}
              </div>
            ))}

            {VALUES.map((val, r) =>
              CATEGORIES.map((cat, c) => {
                const done = answered[`${c}-${r}`];
                return (
                  <button
                    key={`${c}-${r}`}
                    onClick={() => openClue(c, r)}
                    disabled={done}
                    className={done ? "cell done" : "cell"}
                    style={{
                      ...styles.cellBase,
                      ...(done ? styles.cellDone : {}),
                    }}
                  >
                    {done ? "" : `$${val}`}
                  </button>
                );
              })
            )}
          </div>

          <ScorePanel teams={teams} onAward={award} />
        </div>
      )}

      {/* ---------------- CLUE MODAL ---------------- */}
      {active && (
        <div style={styles.overlay} onClick={closeClue}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTag}>
              {CATEGORIES[active.c].title} · ${VALUES[active.r]}
            </div>

            <div style={styles.clueText}>{CATEGORIES[active.c].clues[active.r].clue}</div>

            {!revealed ? (
              <button onClick={() => setRevealed(true)} style={styles.revealBtn}>
                Reveal Answer
              </button>
            ) : (
              <div style={styles.answerText}>
                {CATEGORIES[active.c].clues[active.r].answer}
              </div>
            )}

            {revealed && (
              <div style={styles.scoreRow}>
                <div style={styles.scoreRowLabel}>Award ${VALUES[active.r]} to:</div>
                <div style={styles.scoreBtnGrid}>
                  {teams.map((t, i) => (
                    <div key={i} style={styles.scoreBtnPair}>
                      <span style={styles.scoreTeamName}>{t.name}</span>
                      <div>
                        <button
                          onClick={() => award(i, VALUES[active.r])}
                          style={{ ...styles.markBtn, background: PALETTE.correct }}
                        >
                          ✓ +{VALUES[active.r]}
                        </button>
                        <button
                          onClick={() => award(i, -VALUES[active.r])}
                          style={{ ...styles.markBtn, background: PALETTE.wrong }}
                        >
                          ✕ −{VALUES[active.r]}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={closeClue} style={styles.closeBtn}>
              Back to Board
            </button>
          </div>
        </div>
      )}

      {/* ---------------- FINAL JEOPARDY ---------------- */}
      {screen === "final" && (
        <div style={styles.boardWrap}>
          <div style={styles.finalCard}>
            <div style={styles.kicker}>Final Jeopardy</div>
            <div style={styles.finalCategory}>{FINAL.category}</div>

            {!finalReveal && (
              <>
                <div style={styles.finalInstruction}>
                  Each team secretly writes a wager (0 up to their current score), then enter it below.
                </div>
                <div style={styles.wagerGrid}>
                  {teams.map((t, i) => (
                    <div key={i} style={styles.wagerCell}>
                      <span style={styles.scoreTeamName}>{t.name}</span>
                      <span style={styles.wagerScore}>Score: ${t.score}</span>
                      <input
                        type="number"
                        value={wagers[i] ?? ""}
                        onChange={(e) => setWagers((w) => ({ ...w, [i]: e.target.value }))}
                        placeholder="Wager"
                        style={styles.wagerInput}
                      />
                    </div>
                  ))}
                </div>
                <button onClick={() => setFinalReveal(true)} style={styles.playBtn}>
                  Reveal Clue →
                </button>
              </>
            )}

            {finalReveal && (
              <>
                <div style={styles.clueText}>{FINAL.clue}</div>
                {!finalAnswer ? (
                  <button onClick={() => setFinalAnswer(true)} style={styles.revealBtn}>
                    Reveal Answer
                  </button>
                ) : (
                  <div style={styles.answerText}>{FINAL.answer}</div>
                )}

                {finalAnswer && (
                  <div style={styles.scoreRow}>
                    <div style={styles.scoreRowLabel}>Apply each team's wager:</div>
                    <div style={styles.scoreBtnGrid}>
                      {teams.map((t, i) => {
                        const w = parseInt(wagers[i], 10) || 0;
                        return (
                          <div key={i} style={styles.scoreBtnPair}>
                            <span style={styles.scoreTeamName}>
                              {t.name} (bet ${w})
                            </span>
                            <div>
                              <button
                                onClick={() => award(i, w)}
                                style={{ ...styles.markBtn, background: PALETTE.correct }}
                              >
                                ✓ +{w}
                              </button>
                              <button
                                onClick={() => award(i, -w)}
                                style={{ ...styles.markBtn, background: PALETTE.wrong }}
                              >
                                ✕ −{w}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            <ScorePanel teams={teams} onAward={award} compact />

            <div style={styles.headerBtns}>
              <button onClick={() => setScreen("board")} style={styles.ghostBtn}>
                ← Back to Board
              </button>
              <button onClick={resetGame} style={styles.ghostBtn}>
                New Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SCORE PANEL
// ---------------------------------------------------------------------------
function ScorePanel({ teams, onAward, compact }) {
  const leader = Math.max(...teams.map((t) => t.score));
  return (
    <div style={{ ...styles.scorePanel, ...(compact ? { marginTop: 18 } : {}) }}>
      {teams.map((t, i) => (
        <div
          key={i}
          style={{
            ...styles.scoreCard,
            ...(t.score === leader && leader !== 0 ? styles.scoreCardLead : {}),
          }}
        >
          <div style={styles.scoreCardName}>{t.name}</div>
          <div style={styles.scoreCardVal}>${t.score}</div>
          <div style={styles.scoreNudge}>
            <button onClick={() => onAward(i, 100)} style={styles.nudgeBtn}>
              +100
            </button>
            <button onClick={() => onAward(i, -100)} style={styles.nudgeBtn}>
              −100
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// STYLES
// ---------------------------------------------------------------------------
const globalCss = `
  .cell { transition: transform .08s ease, background .12s ease; }
  .cell:not(.done):hover { background: ${PALETTE.cellHover} !important; transform: translateY(-2px); }
  .cell:not(.done):active { transform: translateY(0); }
  * { box-sizing: border-box; }
`;

const styles = {
  root: {
    fontFamily: FONT_BODY,
    minHeight: "100vh",
    background: `radial-gradient(circle at 50% 0%, ${PALETTE.navy}, ${PALETTE.navyDeep})`,
    color: PALETTE.cream,
    padding: "24px 16px 48px",
  },

  // Title
  titleWrap: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" },
  titleCard: { textAlign: "center", maxWidth: 720, width: "100%" },
  kicker: { letterSpacing: "0.18em", textTransform: "uppercase", color: PALETTE.goldSoft, fontSize: 14, fontWeight: 700 },
  bigTitle: {
    fontFamily: FONT_HEAD, fontSize: "clamp(48px, 11vw, 110px)", color: PALETTE.gold,
    margin: "6px 0 2px", letterSpacing: "0.02em", textShadow: "0 4px 0 rgba(0,0,0,.35)", fontWeight: 700,
  },
  subTitle: { fontFamily: FONT_HEAD, fontSize: "clamp(18px, 3.5vw, 28px)", color: PALETTE.cream, marginBottom: 18 },
  topicRow: { display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 26 },
  topicPill: {
    background: PALETTE.cell, border: `1px solid ${PALETTE.gold}`, borderRadius: 999,
    padding: "6px 14px", fontSize: 14, color: PALETTE.goldSoft,
  },
  setupBox: {
    background: "rgba(0,0,0,.25)", border: `1px solid rgba(212,175,55,.35)`, borderRadius: 14,
    padding: "20px 22px", marginBottom: 24,
  },
  setupLabel: { fontFamily: FONT_HEAD, fontSize: 18, marginBottom: 10 },
  teamBtnRow: { display: "flex", gap: 10, justifyContent: "center", marginBottom: 16 },
  teamCountBtn: {
    width: 48, height: 48, borderRadius: 10, border: `2px solid ${PALETTE.gold}`,
    background: "transparent", color: PALETTE.cream, fontSize: 20, fontWeight: 700, cursor: "pointer",
  },
  teamCountBtnActive: { background: PALETTE.gold, color: PALETTE.navyDeep },
  nameGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 },
  nameInput: {
    padding: "10px 12px", borderRadius: 8, border: `1px solid rgba(212,175,55,.5)`,
    background: PALETTE.navyDeep, color: PALETTE.cream, fontSize: 15, fontFamily: FONT_BODY, textAlign: "center",
  },
  playBtn: {
    fontFamily: FONT_HEAD, fontSize: 22, padding: "14px 36px", borderRadius: 12, border: "none",
    background: PALETTE.gold, color: PALETTE.navyDeep, fontWeight: 700, cursor: "pointer",
    boxShadow: "0 6px 0 rgba(0,0,0,.3)",
  },

  // Board
  boardWrap: { maxWidth: 1100, margin: "0 auto" },
  boardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 },
  boardTitle: { fontFamily: FONT_HEAD, color: PALETTE.gold, fontSize: "clamp(20px, 3vw, 30px)", margin: 0 },
  headerBtns: { display: "flex", gap: 8, flexWrap: "wrap" },
  ghostBtn: {
    background: "transparent", border: `1px solid ${PALETTE.gold}`, color: PALETTE.goldSoft,
    padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontFamily: FONT_BODY,
  },
  finalJumpBtn: {
    background: PALETTE.gold, border: "none", color: PALETTE.navyDeep, padding: "8px 16px",
    borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 700,
  },

  grid: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 24 },
  catHead: {
    background: PALETTE.navyDeep, border: `2px solid ${PALETTE.gold}`, borderRadius: 8,
    padding: "14px 8px", textAlign: "center", fontFamily: FONT_HEAD, fontWeight: 700,
    color: PALETTE.goldSoft, fontSize: "clamp(11px, 1.3vw, 16px)", textTransform: "uppercase",
    letterSpacing: "0.03em", minHeight: 64, display: "flex", alignItems: "center", justifyContent: "center",
  },
  cellBase: {
    background: PALETTE.cell, border: "none", borderRadius: 8, color: PALETTE.gold,
    fontFamily: FONT_HEAD, fontWeight: 700, fontSize: "clamp(20px, 3.2vw, 38px)",
    minHeight: 86, cursor: "pointer", textShadow: "0 2px 0 rgba(0,0,0,.4)",
  },
  cellDone: { background: "rgba(255,255,255,.04)", cursor: "default", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06)" },

  // Modal
  overlay: {
    position: "fixed", inset: 0, background: "rgba(3,10,24,.82)", display: "flex",
    alignItems: "center", justifyContent: "center", padding: 20, zIndex: 50,
  },
  modal: {
    background: `linear-gradient(160deg, ${PALETTE.cell}, ${PALETTE.navyDeep})`,
    border: `3px solid ${PALETTE.gold}`, borderRadius: 16, maxWidth: 760, width: "100%",
    padding: "28px 30px 24px", boxShadow: "0 20px 60px rgba(0,0,0,.6)",
  },
  modalTag: {
    textAlign: "center", color: PALETTE.goldSoft, fontWeight: 700, letterSpacing: "0.08em",
    textTransform: "uppercase", fontSize: 14, marginBottom: 18,
  },
  clueText: {
    fontFamily: FONT_HEAD, fontSize: "clamp(22px, 3.4vw, 34px)", lineHeight: 1.35,
    textAlign: "center", color: PALETTE.cream, margin: "8px 0 24px", minHeight: 80,
  },
  revealBtn: {
    display: "block", margin: "0 auto", background: PALETTE.gold, color: PALETTE.navyDeep,
    border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 18, fontWeight: 700,
    cursor: "pointer", fontFamily: FONT_HEAD,
  },
  answerText: {
    fontFamily: FONT_HEAD, fontSize: "clamp(20px, 3vw, 28px)", textAlign: "center",
    color: PALETTE.goldSoft, background: "rgba(212,175,55,.1)", border: `1px solid rgba(212,175,55,.4)`,
    borderRadius: 10, padding: "16px 18px", margin: "0 0 20px",
  },
  scoreRow: { marginTop: 8 },
  scoreRowLabel: { textAlign: "center", fontSize: 14, color: PALETTE.goldSoft, marginBottom: 10, letterSpacing: "0.04em" },
  scoreBtnGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 },
  scoreBtnPair: {
    background: "rgba(0,0,0,.25)", borderRadius: 10, padding: "10px", textAlign: "center",
    display: "flex", flexDirection: "column", gap: 6,
  },
  scoreTeamName: { fontSize: 14, fontWeight: 700, color: PALETTE.cream },
  markBtn: {
    border: "none", color: "#fff", borderRadius: 7, padding: "7px 10px", margin: "0 3px",
    fontSize: 14, fontWeight: 700, cursor: "pointer",
  },
  closeBtn: {
    display: "block", margin: "22px auto 0", background: "transparent", color: PALETTE.goldSoft,
    border: `1px solid ${PALETTE.gold}`, borderRadius: 8, padding: "9px 22px", cursor: "pointer", fontSize: 14,
  },

  // Score panel
  scorePanel: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 },
  scoreCard: {
    background: PALETTE.navyDeep, border: `1px solid rgba(212,175,55,.3)`, borderRadius: 12,
    padding: "14px 12px", textAlign: "center",
  },
  scoreCardLead: { border: `2px solid ${PALETTE.gold}`, boxShadow: "0 0 20px rgba(212,175,55,.25)" },
  scoreCardName: { fontFamily: FONT_HEAD, fontSize: 16, color: PALETTE.goldSoft, marginBottom: 2 },
  scoreCardVal: { fontFamily: FONT_HEAD, fontSize: 34, fontWeight: 700, color: PALETTE.gold },
  scoreNudge: { marginTop: 6, display: "flex", gap: 6, justifyContent: "center" },
  nudgeBtn: {
    background: "transparent", border: `1px solid rgba(212,175,55,.4)`, color: PALETTE.cream,
    borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer",
  },

  // Final
  finalCard: {
    background: `linear-gradient(160deg, ${PALETTE.cell}, ${PALETTE.navyDeep})`,
    border: `3px solid ${PALETTE.gold}`, borderRadius: 16, padding: "30px 28px", textAlign: "center",
  },
  finalCategory: { fontFamily: FONT_HEAD, fontSize: "clamp(26px, 4vw, 42px)", color: PALETTE.gold, margin: "4px 0 20px" },
  finalInstruction: { fontSize: 15, color: PALETTE.cream, maxWidth: 560, margin: "0 auto 18px", lineHeight: 1.5 },
  wagerGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 22 },
  wagerCell: {
    background: "rgba(0,0,0,.25)", borderRadius: 10, padding: "12px", display: "flex",
    flexDirection: "column", gap: 6, alignItems: "center",
  },
  wagerScore: { fontSize: 13, color: PALETTE.goldSoft },
  wagerInput: {
    width: "100%", padding: "8px", borderRadius: 7, border: `1px solid rgba(212,175,55,.5)`,
    background: PALETTE.navyDeep, color: PALETTE.cream, fontSize: 16, textAlign: "center", fontFamily: FONT_BODY,
  },
};